import { useState, useEffect, useCallback, useMemo } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { Dropdownlist } from '@/components/Dropdownlist';

export interface ClientInfo {
  id?: number | null;
  name?: string;
  hasActivityReportAccess?: boolean;
  hasCaseReportAccess?: boolean;
  hasCallCardAccess?: boolean;
  hasAnalyticsReportAccess?: boolean;
}

export interface ClientListItem {
  id: number;
  name: string;
}

export interface ClientPortalAddEditClientModalProps {
  open: boolean;
  mode?: 'add' | 'edit';
  clientInfo?: ClientInfo;
  clientList?: ClientListItem[];
  selectedClientIds?: number[];
  theme?: string;
  onSave: (data: {
    id: number;
    originalId: number | null;
    name: string;
    hasActivityReportAccess: boolean;
    hasCaseReportAccess: boolean;
    hasCallCardAccess: boolean;
    hasAnalyticsReportAccess: boolean;
    isNpsdClientEnabled: boolean;
  }) => void;
  onCancel: () => void;
}

export function ClientPortalAddEditClientModal({
  open,
  mode = 'add',
  clientInfo = {},
  clientList = [],
  selectedClientIds = [],
  theme,
  onSave,
  onCancel,
}: ClientPortalAddEditClientModalProps) {
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [hasActivityReportAccess, setHasActivityReportAccess] = useState(true);
  const [hasCaseReportAccess, setHasCaseReportAccess] = useState(true);
  const [hasCallCardAccess, setHasCallCardAccess] = useState(true);
  const [hasAnalyticsReportAccess, setHasAnalyticsReportAccess] = useState(true);

  const modalTitle = mode === 'edit' ? 'Edit Client' : 'Add Client';
  const saveButtonText = mode === 'edit' ? 'Edit Client' : 'Add Client';

  // Filter out clients that are already selected (except for the current one being edited)
  const availableClients = useMemo(() => {
    const editingId = mode === 'edit' ? clientInfo?.id : null;
    return (clientList || []).filter((client) => {
      return client.id === editingId || !selectedClientIds.includes(client.id);
    });
  }, [clientList, selectedClientIds, mode, clientInfo?.id]);

  const clientItems = useMemo(
    () =>
      availableClients.map((c) => ({
        label: c.name,
        value: c.id,
      })),
    [availableClients]
  );

  // Reset/populate form when opening
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && clientInfo) {
        setSelectedClientId(clientInfo.id ?? null);
        setHasActivityReportAccess(clientInfo.hasActivityReportAccess !== false);
        setHasCaseReportAccess(clientInfo.hasCaseReportAccess !== false);
        setHasCallCardAccess(clientInfo.hasCallCardAccess !== false);
        setHasAnalyticsReportAccess(clientInfo.hasAnalyticsReportAccess !== false);
      } else {
        // Reset form for add mode - defaults to all access enabled
        setSelectedClientId(null);
        setHasActivityReportAccess(true);
        setHasCaseReportAccess(true);
        setHasCallCardAccess(true);
        setHasAnalyticsReportAccess(true);
      }
    }
  }, [open, mode, clientInfo]);

  const handleSave = useCallback(() => {
    // Validation
    if (selectedClientId === null) {
      (window as any).App?.showPopupNotification?.('Please select a client.', 'error');
      return;
    }

    // Get client name from list
    const selectedClient = availableClients.find((c) => c.id === selectedClientId);
    const clientName = selectedClient ? selectedClient.name : '';

    const data = {
      id: selectedClientId,
      originalId: mode === 'edit' ? (clientInfo?.id ?? null) : null,
      name: clientName,
      hasActivityReportAccess,
      hasCaseReportAccess,
      hasCallCardAccess,
      hasAnalyticsReportAccess,
      isNpsdClientEnabled: true, // New/changed clients are always enabled
    };

    onSave(data);
  }, [
    selectedClientId,
    availableClients,
    mode,
    clientInfo?.id,
    hasActivityReportAccess,
    hasCaseReportAccess,
    hasCallCardAccess,
    hasAnalyticsReportAccess,
    onSave,
  ]);

  return (
    <BaseFormModal
      open={open}
      title={modalTitle}
      theme={theme}
      dialogClass="kstc-dialog--form"
      saveText={saveButtonText}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="kstc-form-group">
        <label className="kstc-label">Client</label>
        <Dropdownlist
          value={selectedClientId}
          items={clientItems}
          placeholder="Select client..."
          searchable={true}
          onChange={(val) => setSelectedClientId(val as number | null)}
        />
      </div>

      <div className="kstc-form-group">
        <div className="kst-checkbox-group">
          <input
            type="checkbox"
            id="cpClientAccessActivityReports"
            className="kstc-checkbox"
            checked={hasActivityReportAccess}
            onChange={(e) => setHasActivityReportAccess(e.target.checked)}
          />
          <label className="kstc-checkbox-label" htmlFor="cpClientAccessActivityReports">
            Access Activity Reports
          </label>
        </div>
        <div className="kst-checkbox-group">
          <input
            type="checkbox"
            id="cpClientAccessCaseReports"
            className="kstc-checkbox"
            checked={hasCaseReportAccess}
            onChange={(e) => setHasCaseReportAccess(e.target.checked)}
          />
          <label className="kstc-checkbox-label" htmlFor="cpClientAccessCaseReports">
            Access Case Reports
          </label>
        </div>
        <div className="kst-checkbox-group">
          <input
            type="checkbox"
            id="cpClientAccessCallsForService"
            className="kstc-checkbox"
            checked={hasCallCardAccess}
            onChange={(e) => setHasCallCardAccess(e.target.checked)}
          />
          <label className="kstc-checkbox-label" htmlFor="cpClientAccessCallsForService">
            Access Calls For Service
          </label>
        </div>
        <div className="kst-checkbox-group">
          <input
            type="checkbox"
            id="cpClientAccessAnalyticsReports"
            className="kstc-checkbox"
            checked={hasAnalyticsReportAccess}
            onChange={(e) => setHasAnalyticsReportAccess(e.target.checked)}
          />
          <label className="kstc-checkbox-label" htmlFor="cpClientAccessAnalyticsReports">
            Access Analytics Reports
          </label>
        </div>
      </div>
    </BaseFormModal>
  );
}

(window as any).ClientPortalAddEditClientModalReact = ClientPortalAddEditClientModal;
